import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  templatesApi,
  FilterTemplateDto,
  CreateTemplateDto,
  UpdateTemplateDto,
} from '../services/api/templatesApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';

// Query keys factory
export const templatesKeys = {
  all: ['templates'] as const,
  lists: () => [...templatesKeys.all, 'list'] as const,
  list: (filters: FilterTemplateDto) =>
    [...templatesKeys.lists(), filters] as const,
  details: () => [...templatesKeys.all, 'detail'] as const,
  detail: (id: string) => [...templatesKeys.details(), id] as const,
};

// Get all templates
export const useTemplates = (filters: FilterTemplateDto = {}) => {
  return useQuery({
    queryKey: templatesKeys.list(filters),
    queryFn: () => templatesApi.getTemplates(filters),
    staleTime: 30000, // 30 seconds
  });
};

// Get single template
export const useTemplate = (id: string) => {
  return useQuery({
    queryKey: templatesKeys.detail(id),
    queryFn: () => templatesApi.getTemplateById(id),
    enabled: !!id,
  });
};

// Create template
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateDto) => templatesApi.createTemplate(data),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: templatesKeys.lists() });
      toast.success('Template created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create template');
    },
  });
};

// Update template
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateDto }) =>
      templatesApi.updateTemplate(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific template and lists
      queryClient.invalidateQueries({
        queryKey: templatesKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: templatesKeys.lists() });
      toast.success('Template updated successfully');

    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update template');
    },
  });
};

// Delete template
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => templatesApi.deleteTemplate(id),
    onSuccess: () => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: templatesKeys.lists() });
      toast.success('Template deleted successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to delete template');
    },
  });
};

// Duplicate template
export const useDuplicateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => templatesApi.duplicateTemplate(id),
    onSuccess: () => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: templatesKeys.lists() });
      toast.success('Template duplicated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to duplicate template');
    },
  });
};


